// crewWeek — the worker × day matrix for a whole week, declared once.
//
// docs/REBUILD_PARITY.md, Schedule: "Crew-by-week view | Schedule
// (CrewWeekView) | Dispatch Board | 🟡". The classic view
// (client/src/components/Schedule/CrewWeekView.js) answers ONE question that
// nothing in the rebuild answered: **who is free this week**. The Dispatch
// Board answers "what is on today and tomorrow, by crew", which is a
// different question and cannot be paged into this one.
//
// ── The contract, read off the route files (not assumed from the names) ─────
//
// GET /api/dispatch/board?date=   server/routes/dispatch.js:122
//   gate    requireDispatch (:23) — admin | manager | permissions.timesheets
//           containing approve / manage_team / manage_all. 403 otherwise.
//   query   `date` ONLY, and only when it matches /^\d{4}-\d{2}-\d{2}$/ (:126);
//           anything else silently becomes today. There is NO `days`
//           parameter — asking for one is not an error, it is ignored.
//   answer  { today, days: [ … ] } with **EXACTLY TWO** days,
//           `[first, addDays(first, 1)]` (:127). This is the whole reason a
//           week needs more than one request.
//   rows    each day is { date, crews: [{ lead, members, jobs }], unassigned }
//           grouped by `j.user_id`, the LEAD (:139). A job with roster members
//           but no lead is in `unassigned`, not under its crew.
//   scope   none. Every dispatcher sees every job — there is no per-user
//           visibility filter in that query.
//
// GET /api/dispatch/approve?week=  server/routes/dispatch.js:161
//   gate    the same requireDispatch.
//   answer  { start_date, end_date, workers: [{ user_id, name, initials, … }] }
//           where start_date is startOfWeekDateString(ref, startOfWeek) (:165)
//           and workers is every `is_active = 1 AND role NOT IN
//           ('admin','accounting')` user, by name (:167).
//
// ── Why not GET /api/jobs/schedule?start=&end=, which answers a whole week ──
//
// Because it is SCOPED. server/routes/jobs.js:194 adds
// `AND (j.user_id = ? OR j.user_id IS NULL)` for anyone who is not admin or
// manager — and a promoted lead holding timesheets.approve is exactly the
// person Dispatch exists for. They would be handed their own two jobs drawn
// as the whole crew's week, with nothing on the screen saying so. Four board
// requests that are right for everybody beat one request that is right for
// some people, and the cost is four cached GETs on a tablet.
//
// ── The rule this file exists for ───────────────────────────────────────────
//
// A week is four requests, so a week can arrive HALF ANSWERED. "Nobody is
// working Thursday" and "we could not ask about Thursday" call for opposite
// actions from a dispatcher, and they look identical in an empty grid. So
// `crewWeek()` tracks which dates were actually answered, refuses to call
// anyone free across a day it does not have, and `weekWarning()` names the
// missing days. Nothing here prints a count over a refusal.

import { addDaysISO } from '../../../utils/scheduleDates';

/** The `date` values to request. Four requests cover eight days; we use 7. */
export const WEEK_DAYS = 7;

/** Exactly what the board returns per request — two days, dispatch.js:127. */
export const BOARD_DAYS_PER_REQUEST = 2;

/** The board takes `date` and nothing else. Named so a screen cannot invent one.
 *
 * These two are SERVER paths, not routes — `/dispatch/*` here is the Express
 * router in server/routes/dispatch.js, which did not move when the client's
 * three apps became one. The `Api` in the name is load-bearing: a rename pass
 * over the client's /dispatch routes rewrote these to `/board` and `/approve`,
 * the board fetched a URL the server has never served, and every dispatch test
 * still passed because its fixture had been rewritten to match. */
export const boardApiPath = (date) => `/dispatch/board${date ? `?date=${encodeURIComponent(date)}` : ''}`;

/** The approve endpoint is where the company's week boundary and roster come from. */
export const approveApiPath = (week) => `/dispatch/approve${week ? `?week=${encodeURIComponent(week)}` : ''}`;

/** The seven ISO dates of the week beginning `startIso`. */
export function weekDays(startIso, n = WEEK_DAYS) {
  if (!startIso) return [];
  return Array.from({ length: n }, (_, i) => addDaysISO(startIso, i));
}

/**
 * The `date` anchors to request so that `weekDays(startIso)` is all covered.
 *
 * Each answer carries two consecutive days, so anchors step by two. Seven days
 * needs four requests and the fourth's second day falls outside the week; it
 * is fetched and ignored rather than trimmed server-side, because the endpoint
 * has no parameter that would trim it.
 */
export function boardAnchors(startIso, n = WEEK_DAYS) {
  if (!startIso) return [];
  const out = [];
  for (let i = 0; i < n; i += BOARD_DAYS_PER_REQUEST) out.push(addDaysISO(startIso, i));
  return out;
}

const list = (a) => (Array.isArray(a) ? a : []);

/**
 * Every job on one day of a board answer, with the roster it carries.
 *
 * `crews` is keyed by LEAD, and a job whose lead is empty sits in
 * `unassigned` even when it has crew on it — so a matrix built from `crews`
 * alone would put a roster member's work under somebody else's name and leave
 * their own row looking empty. Both lists are flattened and re-bucketed by
 * assignee below, which is what makes this a crew view rather than a lead view
 * and what the classic CrewWeekView does (`job.assignees`, falling back to
 * `user_id`).
 */
export function jobsOfDay(day) {
  if (!day) return [];
  const out = [];
  for (const c of list(day.crews)) for (const j of list(c.jobs)) out.push(j);
  for (const j of list(day.unassigned)) out.push(j);
  return out;
}

/**
 * Fold the board answers into one lookup, and say what is MISSING.
 *
 * `answers` is one entry per request: `{ ok, data }` for an answer,
 * `{ ok: false, error }` for a refusal or a dropped connection. A request that
 * did not come back contributes no days, and every date it would have covered
 * is reported in `missing` — never as an empty day.
 *
 * A day present in two answers (it cannot be, given the anchors, but a server
 * that changed its window could produce one) keeps the FIRST, so a re-request
 * cannot silently double a crew's jobs.
 */
export function mergeBoardDays(answers, days) {
  const byDate = new Map();
  let today = null;
  for (const a of list(answers)) {
    if (!a || !a.ok || !a.data) continue;
    if (!today && a.data.today) today = a.data.today;
    for (const d of list(a.data.days)) {
      if (!d || !d.date || byDate.has(d.date)) continue;
      byDate.set(d.date, d);
    }
  }
  const wanted = list(days);
  const missing = wanted.filter((d) => !byDate.has(d));
  return { byDate, today, missing, complete: missing.length === 0 && wanted.length > 0 };
}

/**
 * One row per active worker, plus an Unassigned row, each with a list of jobs
 * per day.
 *
 * Placement mirrors CrewWeekView exactly, because a manager who reads both
 * during the cutover must not be shown two different answers:
 *
 *   - a job lands in the row of EVERY assignee on it (`assignees`), because a
 *     three-person crew is three people who are busy, not one;
 *   - with no roster, it falls back to the lead (`lead_user_id`);
 *   - an assignee with no row — somebody who has left, or an admin, neither of
 *     whom is in the approve roster — does not silently vanish the job. If NO
 *     assignee has a row it goes to Unassigned, because work whose only crew
 *     is gone IS uncovered and is the thing this view exists to surface.
 *
 * `missingDays` are days no answer covered. Their cells are `null` rather than
 * `[]`, so a screen cannot render "free" from a question that was never
 * answered, and `known` counts only the days we actually have.
 */
export const UNASSIGNED_ROW = 'unassigned';

export function crewWeekRows({ workers, days, byDate, missing = [] }) {
  const dayList = list(days);
  const gone = new Set(list(missing));
  const rows = list(workers)
    .map((w) => ({ id: String(w.user_id ?? w.id), name: w.name, initials: w.initials || null }))
    .filter((r) => r.id && r.id !== 'undefined' && r.id !== 'null');
  rows.push({ id: UNASSIGNED_ROW, name: 'Unassigned', initials: null });

  const cells = new Map();
  for (const r of rows) {
    const perDay = new Map();
    for (const d of dayList) perDay.set(d, gone.has(d) ? null : []);
    cells.set(r.id, perDay);
  }

  const has = (id) => cells.has(String(id));

  for (const d of dayList) {
    if (gone.has(d)) continue;
    const day = byDate instanceof Map ? byDate.get(d) : (byDate && byDate[d]);
    for (const job of jobsOfDay(day)) {
      const roster = list(job.assignees).map((a) => String(a.user_id ?? a.id)).filter(Boolean);
      const ids = roster.length ? roster : (job.lead_user_id != null ? [String(job.lead_user_id)] : []);
      const visible = ids.filter(has);
      const targets = visible.length ? visible : [UNASSIGNED_ROW];
      for (const t of targets) {
        const bucket = cells.get(t).get(d);
        if (bucket && !bucket.some((j) => j.id === job.id)) bucket.push(job);
      }
    }
  }

  return rows.map((r) => {
    const perDay = cells.get(r.id);
    const ids = new Set();
    let known = 0;
    for (const d of dayList) {
      const bucket = perDay.get(d);
      if (bucket === null) continue;
      known += 1;
      for (const j of bucket) ids.add(j.id);
    }
    return {
      ...r,
      days: dayList.map((d) => ({ date: d, jobs: perDay.get(d), unknown: perDay.get(d) === null })),
      jobCount: ids.size,
      knownDays: known,
      // "Free all week" is only sayable when every day of the week answered.
      free: known === dayList.length && dayList.length > 0 && ids.size === 0,
    };
  });
}

/** How many jobs a single day holds across every row, de-duped, or null. */
export function dayJobCount(rows, date) {
  const ids = new Set();
  let known = false;
  for (const r of list(rows)) {
    const cell = list(r.days).find((d) => d.date === date);
    if (!cell || cell.jobs === null) continue;
    known = true;
    for (const j of cell.jobs) ids.add(j.id);
  }
  return known ? ids.size : null;
}

/**
 * The sentence to show above a week that did not fully answer, or null.
 *
 * The POSITIVE twin matters as much as the negative one: a complete week
 * returns null and the grid is trusted. This is only ever a warning about days
 * we could not ask about — it never fires because a day was genuinely empty.
 */
export function weekWarning({ missing = [], days = [], error = null } = {}) {
  const gone = list(missing);
  if (gone.length === 0) return null;
  if (gone.length >= list(days).length && list(days).length > 0) {
    return error
      ? `The week could not be loaded: ${error}`
      : 'The week could not be loaded. Nothing below is the crew\'s real week.';
  }
  const which = gone.length === 1 ? gone[0] : `${gone.length} days (${gone.join(', ')})`;
  return error
    ? `${which} did not load: ${error}. Those columns are blank because we could not ask, not because nobody is working.`
    : `${which} did not load. Those columns are blank because we could not ask, not because nobody is working.`;
}

/** Compact meta for a chip: the place, then the block if there is one. */
export function chipMeta(job) {
  if (!job) return null;
  return job.property_name || job.client_name || null;
}
